import type { WorkspaceTreeNode } from "../types.ts";
import { normalizeAgentId } from "../../../../src/routing/session-key.js";

export type ParsedWorkspaceAgent = {
  id: string;
  name?: string;
  model?: string;
  purpose?: string;
  skills?: string[];
  sourcePath: string;
};

const AGENT_YAML_PATH_RE = /(^|\/)agents\/[^/]+\.(ya?ml)$/i;

export function isAgentDefinitionYamlPath(path: string): boolean {
  const normalized = path.trim().replace(/\\/g, "/");
  return AGENT_YAML_PATH_RE.test(normalized);
}

export function collectAgentYamlPaths(nodes: WorkspaceTreeNode[]): string[] {
  const out: string[] = [];
  const walk = (list: WorkspaceTreeNode[]) => {
    for (const node of list) {
      if (node.kind === "dir" && node.children?.length) {
        walk(node.children);
        continue;
      }
      if (node.kind === "file" && isAgentDefinitionYamlPath(node.path)) {
        out.push(node.path);
      }
    }
  };
  walk(nodes);
  return out.toSorted((a, b) => a.localeCompare(b));
}

function parseYamlBlockScalar(
  lines: string[],
  startIndex: number,
): { value: string; nextIndex: number } {
  const first = lines[startIndex]?.trimEnd() ?? "";
  if (!first.endsWith("|") && !first.endsWith(">")) {
    return { value: first, nextIndex: startIndex + 1 };
  }
  const parts: string[] = [];
  let i = startIndex + 1;
  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*\S/.test(line) && !/^\s{2,}/.test(line)) {
      break;
    }
    parts.push(line.replace(/^\s{2}/, "").trimEnd());
    i += 1;
  }
  return { value: parts.join("\n").trim(), nextIndex: i };
}

function parseInlineList(block: string): string[] | undefined {
  const trimmed = block.trim();
  if (!trimmed.startsWith("-")) {
    return undefined;
  }
  return trimmed
    .split("\n")
    .map((line) => line.replace(/^\s*-\s*/, "").trim())
    .filter(Boolean);
}

/** Best-effort parse of Fleet Control agent YAML (`agent.id`, name, model, skills). */
export function parseAgentYamlDefinition(
  content: string,
  sourcePath: string,
): ParsedWorkspaceAgent | null {
  const text = content.trim();
  if (!text) {
    return null;
  }
  const agentSection =
    text.match(/agent:\s*\n([\s\S]*?)(?=\n[a-z_][\w-]*:\s*|\n\S|\s*$)/i)?.[1] ?? text;
  let id = agentSection.match(/^\s*id:\s*["']?([^"'\n#]+)["']?/im)?.[1]?.trim();
  const name = agentSection.match(/^\s*name:\s*["']?([^"'\n#]+)["']?/im)?.[1]?.trim();
  const model = agentSection.match(/^\s*model:\s*["']?([^"'\n#]+)["']?/im)?.[1]?.trim();
  const purposeInline = agentSection.match(/^\s*purpose:\s*([^\n|#]+)/im)?.[1]?.trim();
  let purpose = purposeInline || undefined;
  if (!purpose && /^\s*purpose:\s*\|/im.test(agentSection)) {
    const lines = agentSection.split("\n");
    const start = lines.findIndex((l) => /^\s*purpose:\s*\|/.test(l));
    if (start >= 0) {
      const block = parseYamlBlockScalar(lines, start);
      purpose = block.value || undefined;
    }
  }
  const skillsBlock = agentSection.match(/^\s*skills:\s*\n((?:[ \t]*-[^\n]*\n?)+)/im)?.[1] ?? "";
  const skills = parseInlineList(skillsBlock);
  if (!id) {
    const fallback = sourcePath
      .replace(/\\/g, "/")
      .split("/")
      .pop()
      ?.replace(/\.(ya?ml)$/i, "");
    if (fallback && /^[a-z0-9][a-z0-9_-]{0,63}$/i.test(fallback)) {
      id = fallback;
    }
  }
  if (!id) {
    return null;
  }
  const normalized = normalizeAgentId(id);
  return {
    id: normalized,
    name: name?.trim() || undefined,
    model: model?.trim() || undefined,
    purpose: purpose?.trim() || undefined,
    skills: skills?.length ? skills : undefined,
    sourcePath,
  };
}

export type WorkspaceAgentCandidate = ParsedWorkspaceAgent & {
  alreadyRegistered: boolean;
};

export function markRegisteredCandidates(
  candidates: ParsedWorkspaceAgent[],
  registeredIds: Set<string>,
): WorkspaceAgentCandidate[] {
  return candidates.map((c) => ({
    ...c,
    alreadyRegistered: registeredIds.has(c.id),
  }));
}
